import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { useLocation } from 'react-router-dom';
import { DeleteInventory } from '@/apis/inventory';
import { useToken } from '@/common/helper';
import { useSpin } from '@/components/SpinContext';
import useUnauthorize from '@/hooks/useUnauthorize';
import type {
  InventoryFlatResponse,
  InventoryItemRow,
  InventoryPagination,
  InventoryUnifiedResponse,
} from '../type';

export type ItemRow = InventoryItemRow & {
  key: string;
  rowType: 'item';
  __kind: 'item';
  __depth: number;
};

export type GroupRow = {
  key: string;
  id: string;
  rowType: 'group';
  __kind: 'group';
  __depth: number;
  label: string;
  color?: string;
  groupId: string;
  subgroupId?: string;
  hasChildren: boolean;
  isExpanded: boolean;
  hasSubgroup: boolean;
};

export type LoaderRow = {
  key: string;
  id: string;
  rowType: 'loader';
  __kind: 'loader';
  __depth: number;
  groupId?: string;
  subgroupId?: string;
  label?: string;
};

export type LoadMoreRow = {
  key: string;
  id: string;
  rowType: 'load_more';
  __kind: 'load_more';
  __depth: number;
  groupId?: string;
  subgroupId?: string;
  label: string;
};

export type FlatRow = ItemRow | GroupRow | LoaderRow | LoadMoreRow;

type InventoryTableHookArgs = {
  response?: InventoryUnifiedResponse;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  isLoading?: boolean;
  setShowDetailDrawer?: React.Dispatch<React.SetStateAction<boolean>>;
  deepLinkedInventoryId?: string | null;
  onDeleteSuccess?: () => Promise<void> | void;
  onLoadMoreSubgroups?: (
    groupId: string,
    nextPage: number
  ) => Promise<void> | void;
  onLoadMoreGroups?: (nextPage: number) => Promise<void> | void;
  onLoadMoreGroupItems?: (
    groupId: string,
    nextPage: number
  ) => Promise<void> | void;
  onLoadMoreSubgroupItems?: (
    groupId: string,
    subgroupId: string,
    nextPage: number
  ) => Promise<void> | void;
};

const EMPTY_INVENTORY_RESPONSE: InventoryUnifiedResponse = {
  mode: 'flat',
  groupBy: '',
  subGroupBy: '',
  items: [],
  pagination: {
    page: 0,
    pageSize: 50,
    totalPages: 1,
    totalCount: 0,
  },
};

const hasMorePages = (pagination?: InventoryPagination) => {
  if (!pagination) return false;

  const currentPage = Number(pagination.page ?? 0);
  const totalPages = Number(pagination.totalPages ?? 1);

  return currentPage < totalPages;
};

export const getStatusClassName = (status?: InventoryItemRow['status']) => {
  if (!status) return '';

  switch (status.toLowerCase()) {
    case 'low':
      return 'inventory-status-pill inventory-status-pill--low';
    case 'high':
      return 'inventory-status-pill inventory-status-pill--high';
    default:
      return 'inventory-status-pill';
  }
};

export const useInventoryTable = ({
  response,
  page,
  setPage,
  onLoadMoreSubgroups,
  setShowDetailDrawer,
  deepLinkedInventoryId,
  onDeleteSuccess,
  onLoadMoreGroups,
  onLoadMoreGroupItems,
  onLoadMoreSubgroupItems,
}: InventoryTableHookArgs) => {
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const { unauthorize } = useUnauthorize();
  const location = useLocation();

  const resolvedResponse = response ?? EMPTY_INVENTORY_RESPONSE;

  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedRow, setSelectedRow] = useState<InventoryItemRow | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<InventoryItemRow | null>(
    null
  );
  const [isWideScreen, setIsWideScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1440 : false
  );
  const [isScrollMode, setIsScrollMode] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1320 : false
  );
  const [isMobileTableScroll, setIsMobileTableScroll] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1100 : false
  );
  const [groupExpandedMap, setGroupExpandedMap] = useState<
    Record<string, boolean>
  >({});
  const [subgroupExpandedMap, setSubgroupExpandedMap] = useState<
    Record<string, boolean>
  >({});
  const [loadingRowKey, setLoadingRowKey] = useState<string | null>(null);

  const scrollHostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!deepLinkedInventoryId) return;

    setShowDetailDrawer?.(true);
  }, [deepLinkedInventoryId, setShowDetailDrawer]);

  useEffect(() => {
    const onResize = () => {
      const width = window.innerWidth;
      setIsWideScreen(width >= 1440);
      setIsScrollMode(width < 1320);
      setIsMobileTableScroll(width < 1100);
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if ((location.state as { openAddModal?: boolean } | null)?.openAddModal) {
      setShowDrawer(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (
      resolvedResponse.mode === 'group' ||
      resolvedResponse.mode === 'group_subgroup'
    ) {
      setGroupExpandedMap((prev) => {
        const next = { ...prev };

        resolvedResponse.groups.forEach((group) => {
          if (!(group.id in next)) {
            next[group.id] = false;
          }
        });

        return next;
      });
    }
  }, [resolvedResponse]);

  useEffect(() => {
    if (resolvedResponse.mode === 'group_subgroup') {
      setSubgroupExpandedMap((prev) => {
        const next = { ...prev };

        resolvedResponse.groups.forEach((group) => {
          group.subgroups.forEach((subgroup) => {
            const key = `${group.id}__${subgroup.id}`;

            if (!(key in next)) {
              next[key] = false;
            }
          });
        });

        return next;
      });
    }
  }, [resolvedResponse]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage > 0 ? newPage : 1);
    },
    [setPage]
  );

  const handleDeleteClick = useCallback((team: InventoryItemRow) => {
    setSelectedTeam(team);
    setDeleteModalVisible(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!selectedTeam) return;

    showSpin();

    DeleteInventory(token, selectedTeam.inventoryId)
      .then(async (res) => {
        if (res?.data?.data?.is_success) {
          message.success(res.data.data.message ?? 'Record deleted');
          await onDeleteSuccess?.();
          setShowDetailDrawer?.(false);
        } else {
          message.error(res?.data?.data?.message ?? 'Delete failed');
        }
      })
      .catch((error) => {
        unauthorize(error);
        message.error('Failed to delete record');
      })
      .finally(() => {
        hideSpin();
        setDeleteModalVisible(false);
      });
  }, [
    hideSpin,
    onDeleteSuccess,
    selectedTeam,
    setShowDetailDrawer,
    showSpin,
    token,
    unauthorize,
  ]);

  const handleRowClick = useCallback(
    async (row: FlatRow) => {
      if (row.rowType === 'load_more') {
        setLoadingRowKey(row.key);

        try {
          // Level 3: subgroup items pagination
          if (row.groupId && row.subgroupId) {
            if (resolvedResponse.mode !== 'group_subgroup') return;

            const group = resolvedResponse.groups.find(
              (g) => g.id === row.groupId
            );
            const subgroup = group?.subgroups.find(
              (s) => s.id === row.subgroupId
            );

            if (!subgroup) return;

            const nextPage = Number(subgroup.pagination.page ?? 0) + 1;
            await onLoadMoreSubgroupItems?.(
              row.groupId,
              row.subgroupId,
              nextPage
            );
            return;
          }

          // Level 2: group items / group children pagination
          if (row.groupId) {
            const group =
              resolvedResponse.mode === 'group' ||
              resolvedResponse.mode === 'group_subgroup'
                ? resolvedResponse.groups.find((g) => g.id === row.groupId)
                : null;

            if (!group) return;

            const nextPage = Number(group.pagination.page ?? 0) + 1;

            if (resolvedResponse.mode === 'group_subgroup') {
              await onLoadMoreSubgroups?.(row.groupId, nextPage);
              return;
            }

            if (resolvedResponse.mode === 'group') {
              await onLoadMoreGroupItems?.(row.groupId, nextPage);
              return;
            }

            return;
          }

          // Level 1: top-level groups pagination
          const nextGroupsPage =
            Number(resolvedResponse.pagination.page ?? 1) + 1;
          await onLoadMoreGroups?.(nextGroupsPage);
          return;
        } finally {
          setLoadingRowKey(null);
        }
      }

      if (row.rowType !== 'item') return;

      setSelectedRow(row);
      setShowDrawer(true);
      setShowDetailDrawer?.(true);
    },
    [
      onLoadMoreGroups,
      onLoadMoreGroupItems,
      onLoadMoreSubgroupItems,
      resolvedResponse,
      onLoadMoreSubgroups,
      setShowDetailDrawer,
    ]
  );

  const toggleGroup = useCallback((groupId: string) => {
    setGroupExpandedMap((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  }, []);

  const toggleSubGroup = useCallback((groupId: string, subgroupId: string) => {
    const key = `${groupId}__${subgroupId}`;

    setSubgroupExpandedMap((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const flatRows = useMemo<FlatRow[]>(() => {
    if (resolvedResponse.mode === 'flat') {
      return resolvedResponse.items.map((item) => ({
        ...item,
        key: item.id,
        rowType: 'item',
        __kind: 'item',
        __depth: 0,
      }));
    }

    if (resolvedResponse.mode === 'group') {
      const rows: FlatRow[] = [];

      resolvedResponse.groups.forEach((group) => {
        const isExpanded = !!groupExpandedMap[group.id];

        rows.push({
          key: `group-${group.id}`,
          id: `group-${group.id}`,
          rowType: 'group',
          __kind: 'group',
          __depth: 0,
          label: group.name,
          groupId: group.id,
          color: (group as { color?: string })?.color,
          hasChildren: true,
          isExpanded,
          hasSubgroup: false,
        });

        if (isExpanded) {
          group.items.forEach((item) => {
            rows.push({
              ...item,
              key: `group-item-${group.id}-${item.id}`,
              rowType: 'item',
              __kind: 'item',
              __depth: 1,
            });
          });

          if (hasMorePages(group.pagination)) {
            rows.push({
              key: `load-more-group-${group.id}`,
              id: `load-more-group-${group.id}`,
              rowType: 'load_more',
              __kind: 'load_more',
              __depth: 1,
              groupId: group.id,
              label: 'Load more items',
            });
          }
        }
      });

      if (hasMorePages(resolvedResponse.pagination)) {
        rows.push({
          key: 'load-more-groups-root',
          id: 'load-more-groups-root',
          rowType: 'load_more',
          __kind: 'load_more',
          __depth: 0,
          label: 'Load more groups',
        });
      }

      return rows;
    }

    const rows: FlatRow[] = [];

    resolvedResponse.groups.forEach((group) => {
      const isGroupExpanded = !!groupExpandedMap[group.id];

      rows.push({
        key: `group-${group.id}`,
        id: `group-${group.id}`,
        rowType: 'group',
        __kind: 'group',
        __depth: 0,
        label: group.name,
        groupId: group.id,
        color: (group as { color?: string })?.color,
        hasChildren: true,
        isExpanded: isGroupExpanded,
        hasSubgroup: true,
      });

      if (isGroupExpanded) {
        group.subgroups.forEach((subgroup) => {
          const subgroupKey = `${group.id}__${subgroup.id}`;
          const isSubExpanded = !!subgroupExpandedMap[subgroupKey];

          rows.push({
            key: `subgroup-${group.id}-${subgroup.id}`,
            id: `subgroup-${group.id}-${subgroup.id}`,
            rowType: 'group',
            __kind: 'group',
            __depth: 1,
            label: subgroup.name,
            color: subgroup.color ?? undefined,
            groupId: group.id,
            subgroupId: subgroup.id,
            hasChildren: true,
            isExpanded: isSubExpanded,
            hasSubgroup: false,
          });

          if (isSubExpanded) {
            subgroup.items.forEach((item) => {
              rows.push({
                ...item,
                key: `subgroup-item-${group.id}-${subgroup.id}-${item.id}`,
                rowType: 'item',
                __kind: 'item',
                __depth: 2,
              });
            });

            if (hasMorePages(subgroup.pagination)) {
              rows.push({
                key: `load-more-subgroup-${group.id}-${subgroup.id}`,
                id: `load-more-subgroup-${group.id}-${subgroup.id}`,
                rowType: 'load_more',
                __kind: 'load_more',
                __depth: 2,
                groupId: group.id,
                subgroupId: subgroup.id,
                label: 'Load more subgroup items',
              });
            }
          }
        });

        if (hasMorePages(group.pagination)) {
          rows.push({
            key: `load-more-group-${group.id}`,
            id: `load-more-group-${group.id}`,
            rowType: 'load_more',
            __kind: 'load_more',
            __depth: 1,
            groupId: group.id,
            label: 'Load more subgroups',
          });
        }
      }
    });

    if (hasMorePages(resolvedResponse.pagination)) {
      rows.push({
        key: 'load-more-groups-root',
        id: 'load-more-groups-root',
        rowType: 'load_more',
        __kind: 'load_more',
        __depth: 0,
        label: 'Load more groups',
      });
    }

    return rows;
  }, [groupExpandedMap, resolvedResponse, subgroupExpandedMap]);

  const tableData = useMemo(
    () => flatRows.map((row) => ({ ...row, key: row.key })),
    [flatRows]
  );

  const isFlatMode = resolvedResponse.mode === 'flat';
  const flatPagination = isFlatMode
    ? (resolvedResponse as InventoryFlatResponse).pagination
    : null;

  return {
    deleteModalVisible,
    flatPagination,
    groupExpandedMap,
    handleConfirmDelete,
    handleDeleteClick,
    handlePageChange,
    handleRowClick,
    isFlatMode,
    isScrollMode,
    isMobileTableScroll,
    isWideScreen,
    loadingRowKey,
    localResponse: resolvedResponse,
    page,
    scrollHostRef,
    selectedRow,
    selectedTeam,
    setDeleteModalVisible,
    setShowDrawer,
    showDrawer,
    subgroupExpandedMap,
    tableData,
    toggleGroup,
    toggleSubGroup,
  };
};

export default useInventoryTable;
